<script lang="ts" generics="T">
  import type { Snippet } from 'svelte'

  type Props = {
    items: T[]
    getKey: (item: T) => string
    children: Snippet<[{ item: T, index: number, isDragging: boolean }]>
    onreorder?: (detail: { from: number, to: number }) => void
  }

  const { items, getKey, children, onreorder }: Props = $props()

  let draggedIndex = $state<number | null>(null)
  let dragOverIndex = $state<number | null>(null)
  let sortedItems = $derived(items)

  $effect(() => {
    sortedItems = [...items]
  })

  function handleDragStart(index: number) {
    return (event: DragEvent) => {
      draggedIndex = index
      if (event.dataTransfer) {
        event.dataTransfer.effectAllowed = 'move'
        event.dataTransfer.setData('text/html', '')
      }
    }
  }

  function handleDragOver(index: number) {
    return (event: DragEvent) => {
      event.preventDefault()
      if (draggedIndex === null || draggedIndex === index) return

      dragOverIndex = index

      // Visually reorder items
      const newItems = [...sortedItems]
      const [removed] = newItems.splice(draggedIndex, 1)
      newItems.splice(index, 0, removed)
      sortedItems = newItems
      draggedIndex = index
    }
  }

  function handleDragEnd() {
    if (draggedIndex !== null && draggedIndex !== items.findIndex(item => getKey(item) === getKey(sortedItems[draggedIndex!]))) {
      const originalIndex = items.findIndex(item => getKey(item) === getKey(sortedItems[draggedIndex!]))
      onreorder?.({ from: originalIndex, to: draggedIndex })
    }
    draggedIndex = null
    dragOverIndex = null
  }

  function handleDragLeave() {
    dragOverIndex = null
  }
</script>

<div class="draggable-list">
  {#each sortedItems as item, index (getKey(item))}
    <div
      class="draggable-item"
      class:dragging={draggedIndex === index}
      class:drag-over={dragOverIndex === index}
      draggable={true}
      ondragstart={handleDragStart(index)}
      ondragover={handleDragOver(index)}
      ondragend={handleDragEnd}
      ondragleave={handleDragLeave}
      role="button"
      tabindex={0}
    >
      {@render children({ item, index, isDragging: draggedIndex === index })}
    </div>
  {/each}
</div>

<style lang="scss">
  .draggable-list {
    display: flex;
    flex-direction: column;
    gap: 0;
  }

  .draggable-item {
    cursor: grab;
    transition: opacity 0.2s ease;

    &.dragging {
      opacity: 0.5;
      cursor: grabbing;
    }

    &.drag-over {
      border-top: 2px solid var(--color-primary, #0066cc);
    }
  }
</style>
